import { apiScopes, getMsalInstance } from "../auth/msalConfig";
import { tokenAcquisitionQueue } from "../utils/tokenAcquisitionQueue";
import { TokenExpirationHandler } from "../utils/tokenExpirationHandler";
import { joinUrlSegments } from "../utils/urlUtils";

export interface RequestOptions {
  timeout?: number;
  signal?: AbortSignal;
}

export class BaseService {
  protected apiUrl: string;
  // Default timeout for requests (10 minutes for long-running operations)
  protected defaultTimeout: number = 10 * 60 * 1000; // 10 minutes

  constructor(rootURI: string, endpoint: string) {
    // Use the URL utility for consistent URL construction
    this.apiUrl = joinUrlSegments(rootURI, endpoint);
  }

  /**
   * Get access token with deduplication to prevent cache race conditions
   * Handles multiple concurrent token requests efficiently
   */
  protected async getAccessToken(): Promise<string> {
    const msalInstance = await getMsalInstance();
    const account = msalInstance.getAllAccounts()[0];

    if (!account) {
      throw new Error("No authenticated account found");
    }

    // Use the token acquisition queue to prevent concurrent cache writes
    const tokenResponse = await tokenAcquisitionQueue.acquireTokenSilent(
      msalInstance,
      {
        scopes: apiScopes.backendApi,
        account: account,
      }
    );

    return tokenResponse.accessToken;
  }

  /**
   * Safely constructs a URL by joining the base API URL with a path
   * @param path - The path to append to the API URL
   * @returns The complete URL
   */
  protected buildUrl(path: string = ""): string {
    if (!path) return this.apiUrl;
    return joinUrlSegments(this.apiUrl, path);
  }

  protected getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Add Accept-Language header based on user's selected language
    const currentLanguage = localStorage.getItem("language") || "en";
    headers["Accept-Language"] = currentLanguage;

    return headers;
  }

  /**
   * Safely parse JSON response, handling empty responses gracefully
   */
  protected async parseJsonResponse<T>(response: Response): Promise<T | null> {
    const contentType = response.headers.get("content-type");
    const contentLength = response.headers.get("content-length");

    // Check if response has content
    if (
      contentLength === "0" ||
      !contentType ||
      !contentType.includes("application/json")
    ) {
      return null;
    }

    try {
      const text = await response.text();
      if (!text || text.trim() === "") {
        return null;
      }
      return JSON.parse(text) as T;
    } catch (error) {
      console.warn("Failed to parse JSON response:", error);
      return null;
    }
  }

  // Create an AbortController with timeout
  protected createTimeoutController(timeout?: number): {
    controller: AbortController;
    timeoutId: NodeJS.Timeout;
  } {
    const controller = new AbortController();
    const timeoutMs = timeout || this.defaultTimeout;

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);

    return { controller, timeoutId };
  }

  // Handle fetch with timeout and proper error handling
  protected async fetchWithTimeout(
    url: string,
    options: RequestInit,
    requestOptions: RequestOptions = {}
  ): Promise<Response> {
    const { timeout, signal } = requestOptions;

    // Create timeout controller if no external signal provided
    let timeoutController: AbortController | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    if (!signal) {
      const timeoutSetup = this.createTimeoutController(timeout);
      timeoutController = timeoutSetup.controller;
      timeoutId = timeoutSetup.timeoutId;
      options.signal = timeoutController.signal;
    } else {
      options.signal = signal;
    }

    try {
      const response = await fetch(url, options);

      // Clear timeout if request completed successfully
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      return response;
    } catch (error: any) {
      // Clear timeout on any error
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Handle abort/timeout errors specifically
      if (error.name === "AbortError") {
        // If an external signal caused the abort it was intentional (e.g. component
        // unmounted or deps changed) — re-throw as-is so callers can handle it silently.
        if (signal && signal.aborted) {
          throw error;
        }
        // Otherwise it was our own timeout controller that fired.
        throw new Error(
          `Request timed out after ${(timeout || this.defaultTimeout) / 1000
          } seconds. The search is taking longer than expected. Please try with more specific search terms or contact support if the issue persists.`
        );
      }

      throw error;
    }
  }

  protected async get<T>(
    path: string = "",
    options: RequestOptions = {}
  ): Promise<T> {
    try {
      const accessToken = await this.getAccessToken();

      const headers = {
        ...this.getHeaders(),
        Authorization: `Bearer ${accessToken}`,
      };

      const httpResponse = await this.fetchWithTimeout(
        this.buildUrl(path),
        {
          method: "GET",
          headers,
        },
        options
      );

      if (!httpResponse.ok) {
        throw new Error(`HTTP error! status: ${httpResponse.status}`);
      }

      return (await this.parseJsonResponse<T>(httpResponse)) as T;
    } catch (error) {
      // Always log backend errors for debugging in development
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isHttpError = errorMessage.includes("HTTP error!");
      const statusMatch = errorMessage.match(/status:\s*(\d+)/);
      const statusCode = statusMatch ? parseInt(statusMatch[1]) : 0;

      // Log all errors for debugging, with more detail for backend errors
      if ((error as any).name === "AbortError") {
        // Intentional abort (component unmounted / deps changed) — propagate silently.
        throw error;
      }

      if (isHttpError) {
        console.error(
          `[BaseService] Backend HTTP error ${statusCode} in GET request:`,
          {
            url: this.buildUrl(path),
            error: errorMessage,
            fullError: error,
          }
        );
      } else {
        console.error("[BaseService] GET request error:", error);
      }

      // Only check for token expiration if it's NOT an HTTP error
      // All HTTP errors should be treated as backend issues, not auth issues
      if (
        !isHttpError &&
        TokenExpirationHandler.isTokenExpirationError(error)
      ) {
        console.warn(
          "[BaseService] GET - Genuine token expiration detected, handling..."
        );
        await TokenExpirationHandler.handleTokenExpiration(error);
      }

      throw error;
    }
  }

  protected async post<T, R>(
    data: T,
    path: string = "",
    options: RequestOptions = {}
  ): Promise<R> {
    try {
      const accessToken = await this.getAccessToken();

      const headers = {
        ...this.getHeaders(),
        Authorization: `Bearer ${accessToken}`,
      };

      const httpResponse = await this.fetchWithTimeout(
        this.buildUrl(path),
        {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        },
        options
      );

      if (!httpResponse.ok) {
        const responseBody = await this.parseJsonResponse<{
          errors?: Record<string, string[]>;
          title?: string;
          error?: string;
        }>(httpResponse);
        const err = new Error(`HTTP error! status: ${httpResponse.status}`);
        (err as Error & { responseBody?: unknown }).responseBody = responseBody;
        throw err;
      }

      return (await this.parseJsonResponse<R>(httpResponse)) as R;
    } catch (error) {
      // Always log backend errors for debugging in development
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isHttpError = errorMessage.includes("HTTP error!");
      const statusMatch = errorMessage.match(/status:\s*(\d+)/);
      const statusCode = statusMatch ? parseInt(statusMatch[1]) : 0;

      // Log all errors for debugging, with more detail for backend errors
      if ((error as any).name === "AbortError") {
        // Intentional abort (component unmounted / deps changed) — propagate silently.
        throw error;
      }

      if (isHttpError) {
        console.error(
          `[BaseService] Backend HTTP error ${statusCode} in POST request:`,
          {
            url: this.buildUrl(path),
            error: errorMessage,
            fullError: error,
          }
        );
      } else {
        console.error("[BaseService] POST request error:", error);
      }

      // Only check for token expiration if it's NOT an HTTP error
      // All HTTP errors should be treated as backend issues, not auth issues
      if (
        !isHttpError &&
        TokenExpirationHandler.isTokenExpirationError(error)
      ) {
        console.warn(
          "[BaseService] POST - Genuine token expiration detected, handling..."
        );
        await TokenExpirationHandler.handleTokenExpiration(error);
      }

      throw error;
    }
  }

  protected async put<T, R>(
    data: T,
    path: string = "",
    options: RequestOptions = {}
  ): Promise<R> {
    try {
      const accessToken = await this.getAccessToken();

      const headers = {
        ...this.getHeaders(),
        Authorization: `Bearer ${accessToken}`,
      };

      const httpResponse = await this.fetchWithTimeout(
        this.buildUrl(path),
        {
          method: "PUT",
          headers,
          body: JSON.stringify(data),
        },
        options
      );

      if (!httpResponse.ok) {
        const responseBody = await this.parseJsonResponse<{
          errors?: Record<string, string[]>;
          title?: string;
          error?: string;
        }>(httpResponse);
        const err = new Error(`HTTP error! status: ${httpResponse.status}`);
        (err as Error & { responseBody?: unknown }).responseBody = responseBody;
        throw err;
      }

      return (await this.parseJsonResponse<R>(httpResponse)) as R;
    } catch (error) {
      // Always log backend errors for debugging in development
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isHttpError = errorMessage.includes("HTTP error!");
      const statusMatch = errorMessage.match(/status:\s*(\d+)/);
      const statusCode = statusMatch ? parseInt(statusMatch[1]) : 0;

      // Log all errors for debugging, with more detail for backend errors
      if (isHttpError) {
        console.error(
          `[BaseService] Backend HTTP error ${statusCode} in PUT request:`,
          {
            url: this.buildUrl(path),
            error: errorMessage,
            fullError: error,
          }
        );
      } else {
        console.error("[BaseService] PUT request error:", error);
      }

      // Only check for token expiration if it's NOT an HTTP error
      // All HTTP errors should be treated as backend issues, not auth issues
      if (
        !isHttpError &&
        TokenExpirationHandler.isTokenExpirationError(error)
      ) {
        console.warn(
          "[BaseService] PUT - Genuine token expiration detected, handling..."
        );
        await TokenExpirationHandler.handleTokenExpiration(error);
      }

      throw error;
    }
  }

  protected async patch<T, R>(
    data: T,
    path: string = "",
    options: RequestOptions = {}
  ): Promise<R> {
    try {
      const accessToken = await this.getAccessToken();

      const headers = {
        ...this.getHeaders(),
        Authorization: `Bearer ${accessToken}`,
      };

      const httpResponse = await this.fetchWithTimeout(
        this.buildUrl(path),
        {
          method: "PATCH",
          headers,
          body: JSON.stringify(data),
        },
        options
      );

      if (!httpResponse.ok) {
        const responseBody = await this.parseJsonResponse<{
          errors?: Record<string, string[]>;
          title?: string;
          error?: string;
        }>(httpResponse);
        const err = new Error(`HTTP error! status: ${httpResponse.status}`);
        (err as Error & { responseBody?: unknown }).responseBody = responseBody;
        throw err;
      }

      return (await this.parseJsonResponse<R>(httpResponse)) as R;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isHttpError = errorMessage.includes("HTTP error!");
      const statusMatch = errorMessage.match(/status:\s*(\d+)/);
      const statusCode = statusMatch ? parseInt(statusMatch[1]) : 0;

      if (isHttpError) {
        console.error(
          `[BaseService] Backend HTTP error ${statusCode} in PATCH request:`,
          {
            url: this.buildUrl(path),
            error: errorMessage,
            fullError: error,
          }
        );
      } else {
        console.error("[BaseService] PATCH request error:", error);
      }

      if (
        !isHttpError &&
        TokenExpirationHandler.isTokenExpirationError(error)
      ) {
        console.warn(
          "[BaseService] PATCH - Genuine token expiration detected, handling..."
        );
        await TokenExpirationHandler.handleTokenExpiration(error);
      }

      throw error;
    }
  }

  protected async delete<T>(
    path: string = "",
    options: RequestOptions = {}
  ): Promise<void> {
    try {
      const accessToken = await this.getAccessToken();

      const headers = {
        ...this.getHeaders(),
        Authorization: `Bearer ${accessToken}`,
      };

      const httpResponse = await this.fetchWithTimeout(
        this.buildUrl(path),
        {
          method: "DELETE",
          headers,
        },
        options
      );

      if (!httpResponse.ok) {
        throw new Error(`HTTP error! status: ${httpResponse.status}`);
      }

      // DELETE typically doesn't return content, so we don't need to parse JSON
    } catch (error) {
      // Always log backend errors for debugging in development
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isHttpError = errorMessage.includes("HTTP error!");
      const statusMatch = errorMessage.match(/status:\s*(\d+)/);
      const statusCode = statusMatch ? parseInt(statusMatch[1]) : 0;

      // Log all errors for debugging, with more detail for backend errors
      if (isHttpError) {
        console.error(
          `[BaseService] Backend HTTP error ${statusCode} in DELETE request:`,
          {
            url: this.buildUrl(path),
            error: errorMessage,
            fullError: error,
          }
        );
      } else {
        console.error("[BaseService] DELETE request error:", error);
      }

      // Only check for token expiration if it's NOT an HTTP error
      // All HTTP errors should be treated as backend issues, not auth issues
      if (
        !isHttpError &&
        TokenExpirationHandler.isTokenExpirationError(error)
      ) {
        console.warn(
          "[BaseService] DELETE - Genuine token expiration detected, handling..."
        );
        await TokenExpirationHandler.handleTokenExpiration(error);
      }

      throw error;
    }
  }

  protected async postFormData<T, R>(
    formData: FormData,
    path: string = "",
    options: RequestOptions = {}
  ): Promise<R> {
    try {
      const accessToken = await this.getAccessToken();

      // For FormData, we only need to add Authorization and Accept-Language
      const currentLanguage = localStorage.getItem("language") || "en";
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": currentLanguage,
      };

      const httpResponse = await this.fetchWithTimeout(
        this.buildUrl(path),
        {
          method: "POST",
          headers,
          body: formData,
        },
        options
      );

      if (!httpResponse.ok) {
        throw new Error(`HTTP error! status: ${httpResponse.status}`);
      }

      return (await this.parseJsonResponse<R>(httpResponse)) as R;
    } catch (error) {
      // Always log backend errors for debugging in development
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isHttpError = errorMessage.includes("HTTP error!");
      const statusMatch = errorMessage.match(/status:\s*(\d+)/);
      const statusCode = statusMatch ? parseInt(statusMatch[1]) : 0;

      // Log all errors for debugging, with more detail for backend errors
      if (isHttpError) {
        console.error(
          `[BaseService] Backend HTTP error ${statusCode} in POST FormData request:`,
          {
            url: this.buildUrl(path),
            error: errorMessage,
            fullError: error,
          }
        );
      } else {
        console.error("[BaseService] POST FormData request error:", error);
      }

      // Only check for token expiration if it's NOT an HTTP error
      // All HTTP errors should be treated as backend issues, not auth issues
      if (
        !isHttpError &&
        TokenExpirationHandler.isTokenExpirationError(error)
      ) {
        console.warn(
          "[BaseService] POST FormData - Genuine token expiration detected, handling..."
        );
        await TokenExpirationHandler.handleTokenExpiration(error);
      }

      throw error;
    }
  }

  protected async putFormData<T, R>(
    formData: FormData,
    path: string = "",
    options: RequestOptions = {}
  ): Promise<R> {
    try {
      const accessToken = await this.getAccessToken();

      // For FormData, we only need to add Authorization and Accept-Language
      const currentLanguage = localStorage.getItem("language") || "en";
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": currentLanguage,
      };

      const httpResponse = await this.fetchWithTimeout(
        this.buildUrl(path),
        {
          method: "PUT",
          headers,
          body: formData,
        },
        options
      );

      if (!httpResponse.ok) {
        throw new Error(`HTTP error! status: ${httpResponse.status}`);
      }

      return (await this.parseJsonResponse<R>(httpResponse)) as R;
    } catch (error) {
      // Always log backend errors for debugging in development
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isHttpError = errorMessage.includes("HTTP error!");
      const statusMatch = errorMessage.match(/status:\s*(\d+)/);
      const statusCode = statusMatch ? parseInt(statusMatch[1]) : 0;

      // Log all errors for debugging, with more detail for backend errors
      if (isHttpError) {
        console.error(
          `[BaseService] Backend HTTP error ${statusCode} in PUT FormData request:`,
          {
            url: this.buildUrl(path),
            error: errorMessage,
            fullError: error,
          }
        );
      } else {
        console.error("[BaseService] PUT FormData request error:", error);
      }

      // Only check for token expiration if it's NOT an HTTP error
      // All HTTP errors should be treated as backend issues, not auth issues
      if (
        !isHttpError &&
        TokenExpirationHandler.isTokenExpirationError(error)
      ) {
        console.warn(
          "[BaseService] PUT FormData - Genuine token expiration detected, handling..."
        );
        await TokenExpirationHandler.handleTokenExpiration(error);
      }

      throw error;
    }
  }

  /**
   * Enhanced request method with automatic token refresh retry
   * If a request fails, it will attempt to refresh tokens and retry once
   */
  protected async requestWithRetry<T>(
    method: "GET" | "POST" | "PUT" | "DELETE",
    path: string = "",
    data?: any,
    options: RequestOptions = {}
  ): Promise<T> {
    const maxRetries = 1;
    let attempt = 0;

    while (attempt <= maxRetries) {
      try {
        switch (method) {
          case "GET":
            return await this.get<T>(path, options);
          case "POST":
            return await this.post<any, T>(data, path, options);
          case "PUT":
            return await this.put<any, T>(data, path, options);
          case "DELETE":
            await this.delete(path, options);
            return null as T;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        const isHttpError = errorMessage.includes("HTTP error!");

        // If this is the first attempt and we got an HTTP error,
        // try to refresh tokens and retry once
        if (attempt === 0 && isHttpError) {
          console.log(
            `[BaseService] ${method} request failed, attempting token refresh and retry...`
          );

          try {
            // Try to refresh tokens proactively
            const { TokenExpirationHandler } = await import(
              "../utils/tokenExpirationHandler"
            );
            const refreshed =
              await TokenExpirationHandler.checkAndRefreshTokens();

            if (refreshed) {
              console.log(
                `[BaseService] Token refreshed, retrying ${method} request...`
              );
              attempt++;
              continue; // Retry the request
            }
          } catch (refreshError) {
            console.warn("[BaseService] Token refresh failed:", refreshError);
          }
        }

        // If we get here, either it's not an HTTP error, or retry failed, or this is the second attempt
        throw error;
      }
    }

    throw new Error("Request failed after retry");
  }
}
